import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Briefcase, MapPin, Euro, CheckCircle, Shield, 
  Users, Award, ArrowRight, Sparkles 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  useEffect(() => {
    fetchPublicJobs();
  }, []);

  const fetchPublicJobs = async () => {
    try {
      // Fetch public preview jobs (no auth required, minimal info)
      const response = await axios.get(`${API}/jobs/public/preview`);
      setJobs(response.data);
    } catch (error) {
      console.log('Could not load jobs preview');
    } finally {
      setLoadingJobs(false);
    }
  };

  const features = [
    {
      icon: <Shield className="w-8 h-8 text-primary" />,
      title: 'Verifizierte Jobs',
      description: 'Alle Jobs werden von uns geprüft und begleitet'
    },
    {
      icon: <MapPin className="w-8 h-8 text-secondary-foreground" />,
      title: 'Jobs in deiner Nähe',
      description: 'Finde Jobs direkt in deiner Region oder Uni-Stadt'
    },
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      title: 'Persönliche Betreuung',
      description: 'Wir begleiten dich während des gesamten Bewerbungsprozesses'
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-secondary-foreground" />,
      title: 'Schnelle Vermittlung',
      description: 'Direkter Kontakt zu Arbeitgebern, keine langen Wartezeiten'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-secondary/10">
      {/* Header/Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="UniSide Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-bold font-outfit">UniSide</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/auth')}
              variant="ghost"
              className="rounded-full"
              data-testid="nav-login-button"
            >
              Anmelden
            </Button>
            <Button
              onClick={() => navigate('/auth')}
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg"
              data-testid="nav-register-button"
            >
              Registrieren
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-secondary text-secondary-foreground">
              <Sparkles className="w-3 h-3 mr-1" />
              Kostenlos für Studierende
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold font-outfit mb-6 leading-tight">
              Studentenjobs & Nebenjobs in Österreich
            </h1>
            <p className="text-lg text-slate-600 mb-4">
              Du studierst in Österreich und suchst einen flexiblen <strong>Nebenjob</strong>? 
              Bei <strong>UniSide</strong> findest du verifizierte <strong>Teilzeitjobs</strong>, <strong>Samstagsjobs</strong> und <strong>kurzfristige Jobs</strong>, die perfekt zu deinem Studienalltag passen.
            </p>
            <ul className="text-slate-600 mb-8 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span><strong>Studentenjobs</strong> in Wien, Graz, Linz, Salzburg & ganz Österreich</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>Flexible Arbeitszeiten: <strong>Wochenendjobs</strong>, Abend- & Vormittagsjobs</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span>Persönliche Betreuung vom Verein Zoro</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span><strong>Geringfügige Beschäftigung</strong> & <strong>Teilzeit</strong> – du wählst!</span>
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/auth')}
                size="lg"
                className="h-14 px-8 rounded-full bg-primary hover:bg-primary/90 shadow-lg text-lg"
                data-testid="hero-get-started-button"
              >
                Jetzt Nebenjob finden
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => navigate('/impressum')}
                variant="outline"
                size="lg"
                className="h-14 px-8 rounded-full text-lg"
              >
                Mehr erfahren
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1654559596194-d15e775aaaa0?crop=entropy&cs=tinysrgb&w=800"
                alt="Studenten finden Nebenjobs in Österreich - UniSide Jobplattform"
                className="rounded-2xl shadow-2xl object-cover h-[400px] w-full"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-secondary/30 rounded-full blur-3xl"></div>
            <div className="absolute -top-6 -left-6 w-48 h-48 bg-primary/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-outfit mb-4">Warum UniSide?</h2>
            <p className="text-lg text-slate-600">Die Job-Plattform speziell für Studierende</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-100 shadow-sm rounded-2xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-50 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold font-outfit mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs Preview Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-outfit mb-4">Aktuelle Jobs</h2>
          <p className="text-lg text-slate-600">Registriere dich und finde deinen perfekten Nebenjob</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Dynamic Jobs from Database */}
          {loadingJobs ? (
            // Loading skeleton
            [...Array(3)].map((_, i) => (
              <Card key={i} className="border-slate-100 shadow-sm rounded-2xl animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded mb-2 w-3/4"></div>
                  <div className="h-4 bg-slate-100 rounded mb-4 w-1/2"></div>
                  <div className="h-4 bg-slate-100 rounded mb-2 w-2/3"></div>
                  <div className="h-5 bg-slate-200 rounded w-1/3"></div>
                </CardContent>
              </Card>
            ))
          ) : jobs.length > 0 ? (
            // Real jobs from database (minimal info, no company details)
            jobs.map((job, index) => (
              <Card key={index} className="border-slate-100 shadow-sm rounded-2xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold font-outfit mb-2">{job.title}</h3>
                  {job.categories && job.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {job.categories.map((cat, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{job.location}</span>
                  </div>
                  {job.hoursPerWeek && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                      <Briefcase className="w-4 h-4" />
                      <span>{job.hoursPerWeek}h/Woche</span>
                    </div>
                  )}
                  {job.salary > 0 && (
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      <Euro className="w-4 h-4" />
                      <span>€{job.salary.toLocaleString('de-AT')}/Monat</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            // Fallback sample jobs if no real jobs exist
            <>
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold font-outfit mb-2">Teilzeit-Stellen verfügbar</h3>
                  <p className="text-slate-600 text-sm mb-4">Flexible Arbeitszeiten für Studierende</p>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>Ganz Österreich</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold font-outfit mb-2">Wochenendjobs</h3>
                  <p className="text-slate-600 text-sm mb-4">Ideal neben dem Studium</p>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>Wien, Graz, Linz & mehr</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold font-outfit mb-2">Ferienjobs</h3>
                  <p className="text-slate-600 text-sm mb-4">Saisonale Möglichkeiten</p>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>Österreichweit</span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
        <div className="text-center">
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="h-14 px-8 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
            data-testid="view-all-jobs-button"
          >
            Alle Jobs ansehen
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* About Verein Zoro */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold font-outfit mb-6">Über Verein Zoro</h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                UniSide wird betrieben von <strong>Verein Zoro</strong>, einer Organisation, 
                die sich für die Förderung junger Menschen einsetzt. Wir sind stolz darauf, 
                den <strong className="text-primary">BAWAG PSK Bessere-Antworten Preis</strong> gewonnen zu haben – 
                eine Auszeichnung für besonderes soziales Engagement und Innovation in der Jugendförderung.
              </p>
              <p className="text-slate-400">
                Unser Ziel ist es, Studierenden nicht nur Jobs zu vermitteln, sondern sie auf ihrem 
                Weg in die Arbeitswelt zu begleiten und zu unterstützen.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_uniside-jobs/artifacts/ulkpem59_Bildschirmfoto%202026-02-21%20um%2016.23.20.png"
                alt="Verein Zoro - BAWAG PSK Bessere-Antworten Preis"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-4 -right-4 bg-primary text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                Ausgezeichnet!
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-bold font-outfit mb-6">
          Bereit für deinen neuen Job?
        </h2>
        <p className="text-lg text-slate-600 mb-8">
          Registriere dich kostenlos und finde in wenigen Minuten passende Jobs in deiner Nähe
        </p>
        <Button
          onClick={() => navigate('/auth')}
          size="lg"
          className="h-14 px-8 rounded-full bg-primary hover:bg-primary/90 shadow-lg text-lg"
          data-testid="footer-cta-button"
        >
          Kostenlos registrieren
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </section>

      {/* SEO Keywords Section */}
      <section className="bg-slate-50 py-12 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold font-outfit mb-6 text-center">Beliebte Studentenjobs in Österreich</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-center">
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Teilzeitjobs Wien</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Samstagsjobs</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Wochenendjobs</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Nebenjobs Graz</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Kurzfristige Jobs</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Ferienjobs</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Jobs für Studenten</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Geringfügig</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Studentenjobs Linz</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Werkstudent</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Abendjobs</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-sm">
              <p className="font-medium text-slate-700">Jobs Salzburg</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="UniSide" className="w-8 h-8 object-contain" />
                <span className="text-xl font-bold font-outfit">UniSide</span>
              </div>
              <p className="text-slate-400 text-sm">
                Die Jobplattform für Studenten in Österreich. Finde deinen perfekten Nebenjob neben dem Studium.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Studentenjobs nach Stadt</h3>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>Studentenjobs Wien</li>
                <li>Nebenjobs Graz</li>
                <li>Teilzeitjobs Linz</li>
                <li>Jobs für Studenten Salzburg</li>
                <li>Studentenjobs Innsbruck</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Job-Arten</h3>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>Teilzeitjobs</li>
                <li>Samstagsjobs</li>
                <li>Wochenendjobs</li>
                <li>Kurzfristige Jobs</li>
                <li>Geringfügige Beschäftigung</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex gap-6 text-sm text-slate-400">
              <button onClick={() => navigate('/impressum')} className="hover:text-white transition-colors">
                Impressum
              </button>
              <button onClick={() => navigate('/datenschutz')} className="hover:text-white transition-colors">
                Datenschutz
              </button>
              <button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors">
                Cookies
              </button>
            </div>
            <p className="text-sm text-slate-400">
              © 2026 Verein Zoro. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
